import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Max quantity options in the dropdown when variant has no max (inventory-unlimited).
 * @type {number}
 */
const DEFAULT_SELECT_MAX = 8;

/**
 * A custom element that allows the user to select a quantity via a dropdown (synced to a hidden input).
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLSelectElement} quantitySelect
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'quantitySelect'];
  serverDisabledSelect = false;
  initialized = false;

  /** @type {HTMLLabelElement | null} */
  #dropdownLabel = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.quantitySelect.disabled) {
      this.serverDisabledSelect = true;
    }

    this.#dropdownLabel = this.querySelector('label.quantity-selector__dropdown');
    this.#dropdownLabel?.addEventListener('click', this.#onDropdownLabelClick);

    this.#rebuildSelectOptions();
    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    this.#dropdownLabel?.removeEventListener('click', this.#onDropdownLabelClick);
    this.#dropdownLabel = null;
    super.disconnectedCallback();
  }

  /**
   * Label + `for` still only focus some `<select>`s; opening the native menu needs showPicker() where supported.
   * @param {MouseEvent} event
   */
  #onDropdownLabelClick = (event) => {
    const select = this.refs.quantitySelect;
    if (!(select instanceof HTMLSelectElement) || select.disabled) return;

    if (event.target === select) return;

    if (typeof select.showPicker === 'function') {
      event.preventDefault();
      try {
        select.showPicker();
      } catch {
        select.focus();
      }
    } else {
      select.focus();
    }
  };

  /**
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  getValue() {
    return this.refs.quantityInput.value;
  }

  setValue(value) {
    this.refs.quantityInput.value = value;
    // Rebuild so quantities above DEFAULT_SELECT_MAX gain a matching option.
    this.#rebuildSelectOptions();
  }

  /**
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    let newValue = parseIntOrDefault(quantityInput.value, newMin);

    if ((newValue - newMin) % newStep !== 0) {
      newValue = newMin + Math.floor((newValue - newMin) / newStep) * newStep;
    }

    const effectiveMax = this.getEffectiveMax();
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    quantityInput.value = newValue.toString();
    this.#rebuildSelectOptions();
    this.updateButtonStates();
  }

  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Upper bound for the contiguous dropdown range (inclusive).
   * Capped at {@link DEFAULT_SELECT_MAX} so the list stays short; quantities
   * above that are added as a single extra option in {@link #rebuildSelectOptions}.
   * @returns {number}
   */
  #getSelectRangeUpperBound() {
    const { quantityInput } = this.refs;
    const maxAttr = quantityInput.getAttribute('max');
    const maxRule = maxAttr !== null && maxAttr !== '' ? parseInt(maxAttr, 10) : null;
    const effectiveMax = this.getEffectiveMax();
    const min = parseIntOrDefault(quantityInput.min, 1);

    let upper;
    if (maxRule !== null && effectiveMax !== null) {
      upper = Math.min(maxRule, effectiveMax);
    } else if (maxRule !== null) {
      upper = maxRule;
    } else if (effectiveMax !== null) {
      upper = effectiveMax;
    } else {
      upper = DEFAULT_SELECT_MAX;
    }

    return Math.max(min, Math.min(upper, DEFAULT_SELECT_MAX));
  }

  #rebuildSelectOptions() {
    const { quantityInput, quantitySelect } = this.refs;
    const min = parseIntOrDefault(quantityInput.min, 1);
    const step = parseIntOrDefault(quantityInput.step, 1);
    const rangeUpper = this.#getSelectRangeUpperBound();
    const absoluteMax = this.getEffectiveMax();

    const currentVal = parseIntOrDefault(quantityInput.value, min);
    let snapped = currentVal;
    if ((snapped - min) % step !== 0) {
      snapped = min + Math.floor((snapped - min) / step) * step;
    }
    // Clamp only to real min / inventory max — never to DEFAULT_SELECT_MAX alone,
    // or cart quantities above 8 get silently rewritten to 8.
    snapped = Math.max(min, snapped);
    if (absoluteMax !== null) {
      snapped = Math.min(absoluteMax, snapped);
    }
    quantityInput.value = String(snapped);

    const frag = document.createDocumentFragment();
    for (let q = min; q <= rangeUpper; q += step) {
      const opt = document.createElement('option');
      opt.value = String(q);
      opt.textContent = String(q);
      frag.appendChild(opt);
    }
    // Keep the list short: if qty is above the default range, append it alone
    // (e.g. 1–8, 100) instead of building 1…100.
    if (snapped > rangeUpper) {
      const opt = document.createElement('option');
      opt.value = String(snapped);
      opt.textContent = String(snapped);
      frag.appendChild(opt);
    }
    quantitySelect.replaceChildren(frag);
    this.#syncSelectToInput();
  }

  #syncSelectToInput() {
    const { quantityInput, quantitySelect } = this.refs;
    const v = quantityInput.value;
    if ([...quantitySelect.options].some((o) => o.value === v)) {
      quantitySelect.value = v;
    } else if (quantitySelect.options.length) {
      quantitySelect.selectedIndex = 0;
      quantityInput.value = quantitySelect.value;
    }
  }

  updateButtonStates() {
    const { quantityInput, quantitySelect } = this.refs;
    if (!this.serverDisabledSelect) {
      quantitySelect.disabled = quantityInput.disabled;
    }
  }

  /**
   * @param {Event} event
   */
  onSelectChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    this.refs.quantityInput.value = event.target.value;
    this.onQuantityChange();
    this.updateButtonStates();
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value, 10);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let clampedValue = Math.max(min, value);
    if (effectiveMax !== null) {
      clampedValue = Math.min(effectiveMax, clampedValue);
    }

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.#rebuildSelectOptions();
    this.updateButtonStates();
  }

  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
